import React, { useEffect, useMemo, useState } from 'react';
import { useAdminArticles } from '../../hooks/useAdminArticles.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const DEFAULT_IMAGE = <div className="table-image-placeholder"><i data-lucide="image-off" /></div>;
const WEEK_DAYS = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' }
];

const buildDefaultHours = () => WEEK_DAYS.map((day) => ({
  day_of_week: day.value,
  day_name: day.label,
  start_time: '08:00',
  end_time: '22:00',
  active: false
}));

const mergeHoursWithDefaults = (incoming = []) => {
  const base = new Map(buildDefaultHours().map((item) => [item.day_of_week, { ...item }]));
  (incoming || []).forEach((item) => {
    const day = Number(item.day_of_week ?? item.day);
    if (!day || !base.has(day)) {
      return;
    }
    const current = base.get(day);
    base.set(day, {
      ...current,
      start_time: item.start_time || item.hora_inicio || current.start_time,
      end_time: item.end_time || item.hora_fin || current.end_time,
      active: item.active ?? true
    });
  });
  return Array.from(base.values());
};

const ArticlesSection = ({ onNotify }) => {
  const {
    items,
    processes,
    categories,
    processFilter,
    setProcessFilter,
    loading,
    error,
    refresh,
    saveArticle,
    deleteArticle,
    uploadImage,
    fetchOperatingHours,
    saveOperatingHours
  } = useAdminArticles();

  const [search, setSearch] = useState('');
  const [editorState, setEditorState] = useState({ open: false, record: null, saving: false, imageUploading: false });

  useLucideIcons([items.length, search, editorState.open, editorState.record?.imagen_url]);

  const filteredItems = useMemo(() => {
    const query = search.toLowerCase();
    return items.filter((article) => {
      if (!query) {
        return true;
      }
      const name = article.nombre?.toLowerCase() || '';
      const category = article.categoria?.toLowerCase() || '';
      const process = article.proceso_nombre?.toLowerCase() || '';
      return name.includes(query) || category.includes(query) || process.includes(query);
    });
  }, [items, search]);

  const handleOpenModal = (record = null) => {
    setEditorState({
      open: true,
      saving: false,
      imageUploading: false,
      record: record
        ? { ...record }
        : {
          proceso_id: '',
          nombre: '',
          articulo_id_boh: '',
          categoria_id: '',
          tiempo_vida_minutos: 0,
          tiempo_preparacion_min: 0,
          porcion: 0,
          unidad: '',
          unidades: '',
          tiene_horarios_configurados: false,
          activo: true,
          imagen_url: ''
        }
    });
  };

  const handleSave = async ({ article, operatingHours }) => {
    setEditorState((prev) => ({ ...prev, saving: true }));
    try {
      if (!article) {
        throw new Error('Información del artículo incompleta.');
      }
      const result = await saveArticle(article);
      const articleId = article?.id || result?.id;
      if (!articleId) {
        throw new Error('No se pudo obtener el ID del artículo.');
      }
      if (operatingHours) {
        await saveOperatingHours(articleId, operatingHours);
      }
      setEditorState({ open: false, record: null, saving: false, imageUploading: false });
      refresh();
      onNotify?.({
        type: 'success',
        message: article?.id ? 'Artículo actualizado correctamente.' : 'Artículo creado correctamente.'
      });
    } catch (err) {
      setEditorState((prev) => ({ ...prev, saving: false }));
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo guardar el artículo.' });
    }
  };

  const handleDelete = async (record) => {
    const confirmed = window.confirm(`¿Eliminar el artículo "${record.nombre}"?`);
    if (!confirmed) {
      return;
    }
    try {
      await deleteArticle(record.id);
      refresh();
      onNotify?.({ type: 'success', message: 'Artículo eliminado.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo eliminar el artículo.' });
    }
  };

  const closeModal = () => {
    setEditorState({ open: false, record: null, saving: false, imageUploading: false });
  };

  return (
    <section id="articles-section" className="admin-section active">
      <div className="section-header">
        <h2 className="section-title">Artículos</h2>
        <div className="section-actions">
          <div className="search-container">
            <span className="search-icon"><i data-lucide="search" /></span>
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por nombre, categoría o proceso..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search ? (
              <button type="button" className="btn-secondary" onClick={() => setSearch('')}>
                Limpiar
              </button>
            ) : null}
          </div>
          <div className="button-group">
            <div className="form-group">
              <select
                className="form-select"
                value={processFilter}
                onChange={(event) => setProcessFilter(event.target.value)}
              >
                <option value="">Todos los procesos</option>
                {processes.map((process) => (
                  <option key={process.id} value={process.id}>
                    {process.nombre}
                  </option>
                ))}
              </select>
            </div>
            <button className="btn-primary" type="button" onClick={() => handleOpenModal()}>
              <span className="btn-icon"><i data-lucide="plus" /></span>
              Nuevo Artículo
            </button>
          </div>
        </div>
      </div>
      <div className="data-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Cargando artículos...</div>
          </div>
        ) : (
          <table className="data-table" id="articles-table">
            <thead>
              <tr>
                <th>Imagen</th>
                <th>Nombre</th>
                <th>ID BoH</th>
                <th>Tiempo de vida</th>
                <th>Horarios</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={7} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                    <div className="empty-state-text">{error}</div>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="package" /></div>
                    <div className="empty-state-text">No hay artículos configurados.</div>
                    <div className="empty-state-subtext">Crea tu primer artículo para comenzar.</div>
                  </td>
                </tr>
              ) : (
                filteredItems.map((article) => (
                  <tr key={article.id}>
                    <td>
                      {article.imagen_url ? (
                        <img src={article.imagen_url} alt={article.nombre} className="table-image" />
                      ) : DEFAULT_IMAGE}
                    </td>
                    <td>{article.nombre}</td>
                    <td>{article.articulo_id_boh || '-'}</td>
                    <td><span className="time-display-small">{article.tiempo_vida_minutos} min</span></td>
                    <td>
                      <span className={`hours-badge ${article.tiene_horarios_configurados ? 'hours-limited' : 'hours-open'}`}>
                        {article.tiene_horarios_configurados ? 'Limitado' : '24/7'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${article.activo ? 'status-active' : 'status-inactive'}`}>
                        {article.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn edit" type="button" onClick={() => handleOpenModal(article)}>
                          <i data-lucide="edit" />
                        </button>
                        <button className="action-btn delete" type="button" onClick={() => handleDelete(article)}>
                          <i data-lucide="trash-2" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {editorState.open ? (
        <ArticleModal
          state={editorState}
          processes={processes}
          categories={categories}
          onClose={closeModal}
          onSubmit={handleSave}
          onUpload={uploadImage}
          onNotify={onNotify}
          onLoadHours={fetchOperatingHours}
        />
      ) : null}
    </section>
  );
};

const ArticleModal = ({ state, processes, categories, onClose, onSubmit, onUpload, onNotify, onLoadHours }) => {
  const toInputString = (value) => (value === undefined || value === null ? '' : String(value));

  const [formValues, setFormValues] = useState(() => ({
    ...state.record,
    proceso_id: state.record?.proceso_id || '',
    nombre: state.record?.nombre || '',
    articulo_id_boh: toInputString(state.record?.articulo_id_boh),
    categoria_id: state.record?.categoria_id || '',
    tiempo_vida_minutos: state.record?.tiempo_vida_minutos || 0,
    tiempo_preparacion_min: state.record?.tiempo_preparacion_min || 0,
    porcion: state.record?.porcion || 0,
    unidad: state.record?.unidad || '',
    unidades: state.record?.unidades || '',
    activo: state.record?.activo ?? true,
    imagen_url: state.record?.imagen_url || ''
  }));

  const [imageState, setImageState] = useState({ uploading: false });
  const [hoursState, setHoursState] = useState(() => ({
    enabled: Boolean(state.record?.tiene_horarios_configurados),
    items: mergeHoursWithDefaults(),
    loading: Boolean(state.record?.id),
    error: ''
  }));

  const hoursItems = hoursState.items && hoursState.items.length > 0
    ? hoursState.items
    : mergeHoursWithDefaults();

  const activeDays = hoursItems.filter((item) => item.active);
  const availabilityLabel = hoursState.enabled
    ? activeDays.length
      ? `${activeDays.length}/7 días activos`
      : 'Sin días abiertos'
    : 'Disponible 24/7';
  const availabilityTone = hoursState.enabled
    ? activeDays.length
      ? 'pill-limited'
      : 'pill-empty'
    : 'pill-open';
  const availabilityDetail = hoursState.enabled
    ? 'Solo se podrá producir dentro de los intervalos que definas abajo.'
    : 'Si no activas restricciones, el artículo estará disponible todo el día.';

  useEffect(() => {
    let active = true;
    if (!state.record?.id || !onLoadHours) {
      setHoursState((prev) => ({ ...prev, loading: false }));
      return () => { active = false; };
    }
    setHoursState((prev) => ({ ...prev, loading: true, error: '' }));
    onLoadHours(state.record.id)
      .then((articleData) => {
        if (!active) return;
        const mergedHours = mergeHoursWithDefaults(articleData?.operating_hours);
        const enabled = Boolean(articleData?.tiene_horarios_configurados) || mergedHours.some((item) => item.active);
        setHoursState({
          enabled,
          items: mergedHours,
          loading: false,
          error: ''
        });
      })
      .catch((err) => {
        if (!active) return;
        setHoursState((prev) => ({
          ...prev,
          loading: false,
          error: err?.message || 'No se pudieron cargar los horarios'
        }));
      });
    return () => {
      active = false;
    };
  }, [state.record?.id, onLoadHours]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setImageState({ uploading: true });
    try {
      const data = await onUpload({ file, entityId: formValues.id });
      setFormValues((prev) => ({ ...prev, imagen_url: data.image_url }));
      onNotify?.({ type: 'success', message: data.message || 'Imagen subida correctamente.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo subir la imagen.' });
    } finally {
      setImageState({ uploading: false });
      event.target.value = '';
    }
  };

  const handleRemoveImage = () => {
    setFormValues((prev) => ({ ...prev, imagen_url: '' }));
  };

  const toggleHoursEnabled = (event) => {
    const checked = event.target.checked;
    setHoursState((prev) => {
      const hasActiveDay = prev.items.some((item) => item.active);
      return {
        ...prev,
        enabled: checked,
        items: checked && !hasActiveDay
          ? prev.items.map((item) => ({ ...item, active: true }))
          : prev.items
      };
    });
  };

  const handleHoursChange = (day, field, value) => {
    setHoursState((prev) => ({
      ...prev,
      items: prev.items.map((item) => (
        item.day_of_week === day ? { ...item, [field]: value } : item
      ))
    }));
  };

  const handleToggleDay = (day, checked) => {
    handleHoursChange(day, 'active', checked);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      article: { ...formValues, id: state.record?.id || null },
      operatingHours: {
        enabled: hoursState.enabled,
        items: hoursState.items
      }
    });
  };

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content" style={{ maxWidth: '820px' }}>
        <div className="modal-header">
          <h3>{state.record?.id ? 'Editar Artículo' : 'Nuevo Artículo'}</h3>
          <button className="modal-close" type="button" onClick={onClose}>
            <i data-lucide="x" />
          </button>
        </div>
        <form className="modal-body form-grid" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Proceso</label>
            <select
              className="form-select"
              name="proceso_id"
              value={formValues.proceso_id}
              onChange={handleChange}
              required
            >
              <option value="">Seleccionar proceso</option>
              {processes.map((process) => (
                <option key={process.id} value={process.id}>
                  {process.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Nombre</label>
            <input
              type="text"
              className="form-input"
              name="nombre"
              value={formValues.nombre}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">ID BoH</label>
            <input
              type="text"
              className="form-input"
              name="articulo_id_boh"
              value={formValues.articulo_id_boh}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Categoría</label>
            <select
              className="form-select"
              name="categoria_id"
              value={formValues.categoria_id || ''}
              onChange={handleChange}
            >
              <option value="">Sin categoría</option>
              {categories?.map((category) => (
                <option key={category.id} value={category.id} disabled={!category.activo}>
                  {category.nombre}{category.activo ? '' : ' (inactiva)'}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Tiempo de vida (min)</label>
            <input
              type="number"
              className="form-input"
              name="tiempo_vida_minutos"
              min="0"
              value={formValues.tiempo_vida_minutos}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Tiempo de preparación (min)</label>
            <input
              type="number"
              className="form-input"
              name="tiempo_preparacion_min"
              min="0"
              value={formValues.tiempo_preparacion_min}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Porción</label>
            <input
              type="number"
              className="form-input"
              name="porcion"
              min="0"
              step="0.01"
              value={formValues.porcion}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Unidad</label>
            <input
              type="text"
              className="form-input"
              name="unidad"
              value={formValues.unidad}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Unidades</label>
            <input
              type="text"
              className="form-input"
              name="unidades"
              value={formValues.unidades}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="activo"
                checked={formValues.activo}
                onChange={handleChange}
              />
              <span className="checkmark" />
              <span className="checkbox-label">Artículo activo</span>
            </label>
          </div>

          <div className="form-group full-width">
            <label className="form-label">Imagen</label>
            <div className="image-upload-area" id="article-image-upload">
              {formValues.imagen_url ? (
                <div className="image-preview" id="article-image-preview">
                  <img src={formValues.imagen_url} alt={formValues.nombre || 'Artículo'} className="image-preview-img" />
                  <button className="image-preview-remove" type="button" onClick={handleRemoveImage}>
                    <i data-lucide="x" />
                  </button>
                </div>
              ) : (
                <div className="image-placeholder">
                  <i data-lucide="image" />
                  <p>Arrastra una imagen o selecciónala</p>
                </div>
              )}
              <div className="image-actions">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={imageState.uploading}
                />
                {imageState.uploading ? (
                  <span className="uploading-text">Subiendo...</span>
                ) : null}
              </div>
            </div>
          </div>

          {/* Operating Hours Section - Full Width */}
          <div className="form-group full-width operating-hours-card">
            <div className="operating-hours-header ive-style">
              <div className="availability-copy">
                <span className="eyebrow-label">Restricción de horarios</span>
                <div className="availability-title-row">
                  <label className="form-label">Disponibilidad del artículo</label>
                  <span className={`availability-pill ${availabilityTone}`}>
                    {availabilityLabel}
                  </span>
                </div>
                <p className="field-hint">{availabilityDetail}</p>
              </div>
              <div className="availability-toggle">
                <span className="toggle-label">{hoursState.enabled ? 'Horario controlado' : 'Modo 24/7'}</span>
                <label className="ios-switch">
                  <input
                    type="checkbox"
                    checked={hoursState.enabled}
                    onChange={toggleHoursEnabled}
                    aria-label="Restringir horarios"
                  />
                  <span className="ios-switch-slider" />
                </label>
                <span className="toggle-subtext">
                  {hoursState.enabled ? 'Activa solo los días y horas disponibles' : 'Desactiva para operar sin límites'}
                </span>
              </div>
            </div>

            {hoursState.loading ? (
              <div className="loading-inline">
                <div className="loading-spinner small" />
                <span className="loading-text">Cargando horarios...</span>
              </div>
            ) : null}
            {hoursState.error ? (
              <div className="inline-alert">
                <i data-lucide="alert-triangle" />
                <span>{hoursState.error}</span>
              </div>
            ) : null}

            <div className={`operating-hours-grid ${!hoursState.enabled ? 'disabled' : ''}`}>
              {hoursItems.map((item) => (
                <div key={item.day_of_week} className="operating-hours-row">
                  <div className="day-block">
                    <div className="day-name">{item.day_name}</div>
                    <div className={`day-chip ${item.active && hoursState.enabled ? 'chip-open' : 'chip-closed'}`}>
                      {item.active && hoursState.enabled ? 'Abierto' : 'Cerrado'}
                    </div>
                  </div>
                  <div className="toggle-block">
                    <label className="checkbox-item inline tight">
                      <input
                        type="checkbox"
                        checked={item.active}
                        disabled={!hoursState.enabled}
                        onChange={(event) => handleToggleDay(item.day_of_week, event.target.checked)}
                      />
                      <span className="checkmark" />
                      <span className="checkbox-label">Habilitar</span>
                    </label>
                  </div>
                  <div className="hours-block">
                    <div className="hour-inputs">
                      <span className="time-label">De</span>
                      <input
                        type="time"
                        className="time-input compact"
                        value={item.start_time}
                        disabled={!hoursState.enabled || !item.active}
                        onChange={(event) => handleHoursChange(item.day_of_week, 'start_time', event.target.value)}
                      />
                      <span className="time-separator">a</span>
                      <input
                        type="time"
                        className="time-input compact"
                        value={item.end_time}
                        disabled={!hoursState.enabled || !item.active}
                        onChange={(event) => handleHoursChange(item.day_of_week, 'end_time', event.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn-secondary" type="button" onClick={onClose} disabled={state.saving}>
              Cancelar
            </button>
            <button
              className="btn-primary"
              type="submit"
              disabled={state.saving || imageState.uploading || hoursState.loading}
            >
              {state.saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArticlesSection;
